/**
 * CSRF recovery for the CRM (REM-1710).
 *
 * A 419 means the request carried a token the session no longer has. Instead of
 * showing it, the client asks /crm/session/token for the current one and replays
 * the request once. If the session behind a logged-in page is gone, it goes to
 * /crm/session/expired (login with a message). If the replay fails again, it
 * reloads the page once; a sessionStorage stamp turns a second reload within
 * RELOAD_GUARD_MS into the expired route, so there is never a loop.
 *
 * Server contract: bootstrap/app.php (JSON 419), CsrfTokenController,
 * SessionExpiredController.
 */

export const CSRF_MISMATCH_STATUS = 419;
export const TOKEN_URL = '/crm/session/token';
export const EXPIRED_URL = '/crm/session/expired';
export const RELOAD_GUARD_KEY = 'crm.csrf.reloadedAt';
export const RELOAD_GUARD_MS = 60_000;

const RETRIED_FLAG = '__csrfRetried';
const SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS'];

/** A promise that never settles: the page is navigating away. */
const navigatingAway = () => new Promise(() => {});

/**
 * @param {object} deps
 * @param {() => Promise<{token: string, authenticated: boolean}>} deps.fetchToken
 * @param {() => boolean} deps.isAuthenticatedPage whether the current page was rendered for a logged-in user
 * @param {(url: string) => void} deps.navigate
 * @param {() => void} deps.reload
 * @param {(token: string) => void} deps.setMetaToken
 * @param {Storage|null} deps.storage
 * @param {() => number} [deps.now]
 */
export function createCsrfRecovery({
    fetchToken,
    isAuthenticatedPage,
    navigate,
    reload,
    setMetaToken,
    storage,
    now = () => Date.now(),
}) {
    let pending = null;

    function renewToken() {
        if (!pending) {
            pending = Promise.resolve()
                .then(fetchToken)
                .finally(() => {
                    pending = null;
                });
        }

        return pending;
    }

    function readStamp() {
        try {
            return Number(storage?.getItem(RELOAD_GUARD_KEY)) || 0;
        } catch {
            return 0;
        }
    }

    function writeStamp(value) {
        try {
            storage?.setItem(RELOAD_GUARD_KEY, String(value));
        } catch {
            // Storage blocked: the reload still happens, only unguarded.
        }
    }

    /** Last resort: reload once, or go to login if we just reloaded. */
    function giveUp() {
        const last = readStamp();

        if (last && now() - last < RELOAD_GUARD_MS) {
            navigate(EXPIRED_URL);
            return 'expired';
        }

        writeStamp(now());
        reload();
        return 'reload';
    }

    /**
     * @returns {Promise<{action: 'retry', token: string} | {action: 'expired'} | {action: 'reload'}>}
     */
    async function recover() {
        let result;

        try {
            result = await renewToken();
        } catch {
            return { action: giveUp() };
        }

        if (!result || typeof result.token !== 'string' || result.token === '') {
            return { action: giveUp() };
        }

        if (!result.authenticated && isAuthenticatedPage()) {
            navigate(EXPIRED_URL);
            return { action: 'expired' };
        }

        setMetaToken(result.token);
        return { action: 'retry', token: result.token };
    }

    return { recover, giveUp };
}

/**
 * Replays an axios request once after a 419. Inertia's router and useForm use
 * the default axios instance, so this covers them too.
 */
export function installAxiosCsrfRecovery(axios, recovery) {
    return axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const { response, config } = error ?? {};

            if (!response || response.status !== CSRF_MISMATCH_STATUS || !config) {
                throw error;
            }

            if (config[RETRIED_FLAG]) {
                recovery.giveUp();
                return navigatingAway();
            }

            const outcome = await recovery.recover();

            if (outcome.action !== 'retry') {
                return navigatingAway();
            }

            config[RETRIED_FLAG] = true;
            setAxiosHeader(config, 'X-CSRF-TOKEN', outcome.token);

            return axios.request(config);
        },
    );
}

function setAxiosHeader(config, name, value) {
    if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set(name, value);
        return;
    }

    config.headers = { ...(config.headers ?? {}), [name]: value };
}

/** Reads the XSRF-TOKEN cookie Laravel refreshes on every response. */
export function readXsrfCookie(cookieString) {
    const match = (cookieString ?? '').match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);

    if (!match) {
        return '';
    }

    try {
        return decodeURIComponent(match[1]);
    } catch {
        return '';
    }
}

function isSameOriginUrl(input, origin) {
    try {
        return new URL(String(input), origin).origin === origin;
    } catch {
        return false;
    }
}

function canReplayBody(body) {
    return body === undefined
        || body === null
        || typeof body === 'string'
        || (typeof FormData !== 'undefined' && body instanceof FormData)
        || (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)
        || (typeof Blob !== 'undefined' && body instanceof Blob)
        || body instanceof ArrayBuffer;
}

/**
 * The meta csrf-token tag is rendered once per full page load and goes stale
 * after an in-app login; X-CSRF-TOKEN beats the cookie on the server. When the
 * request carries it and the cookie exists, send the cookie instead, as axios does.
 */
export function withCurrentCsrfHeader(init, xsrfCookie) {
    const headers = new Headers(init.headers ?? {});

    if (!headers.has('X-CSRF-TOKEN') || !xsrfCookie) {
        return init;
    }

    headers.delete('X-CSRF-TOKEN');
    headers.set('X-XSRF-TOKEN', xsrfCookie);

    return { ...init, headers };
}

function withToken(init, token) {
    const headers = new Headers(init.headers ?? {});
    headers.delete('X-XSRF-TOKEN');
    headers.set('X-CSRF-TOKEN', token);

    if (typeof FormData !== 'undefined' && init.body instanceof FormData && init.body.has('_token')) {
        init.body.set('_token', token);
    }

    return { ...init, headers };
}

/**
 * Wraps fetch for the same-origin, state-changing requests the CRM sends with a
 * CSRF token. Anything else goes straight to the original fetch.
 */
export function createCsrfFetch(originalFetch, recovery, { origin, readCookie }) {
    return async function csrfFetch(input, init = {}) {
        const method = String(init.method ?? 'GET').toUpperCase();
        const handled = (typeof input === 'string' || input instanceof URL)
            && !SAFE_METHODS.includes(method)
            && isSameOriginUrl(input, origin);

        if (!handled) {
            return originalFetch(input, init);
        }

        const first = await originalFetch(input, withCurrentCsrfHeader(init, readCookie()));

        if (first.status !== CSRF_MISMATCH_STATUS) {
            return first;
        }

        if (!canReplayBody(init.body)) {
            recovery.giveUp();
            return navigatingAway();
        }

        const outcome = await recovery.recover();

        if (outcome.action !== 'retry') {
            return navigatingAway();
        }

        const second = await originalFetch(input, withToken(init, outcome.token));

        if (second.status === CSRF_MISMATCH_STATUS) {
            recovery.giveUp();
            return navigatingAway();
        }

        return second;
    };
}

/** Production wiring of the token request against the server contract. */
export async function requestCsrfToken(originalFetch) {
    const response = await originalFetch(TOKEN_URL, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) {
        throw new Error(`CSRF token request failed with ${response.status}`);
    }

    return response.json();
}
